import React, { useState, useEffect } from 'react';
import { Card, Container, Nav, Row, Col, Badge, Alert, Button, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, onValue, set, remove, push } from 'firebase/database';
import { Link, useLocation } from 'react-router-dom';
import Calendar from 'react-calendar';
import '../styles/calendar.css';

interface TransportMeans {
  id: string;
  name: string;
  vega: boolean;
}

interface WorkLocation {
  id: string;
  name: string;
  color: string;
  is_active?: boolean;
}

interface WorkDay {
  date: string;
  locationId: string;
  transportToWorkId?: string;
  transportToHomeId?: string;
  notes?: string;
}

const WorkDashboard = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const activeWorkLocations = workLocations.filter((loc) => loc.is_active !== false);
  const [workDays, setWorkDays] = useState<Record<string, WorkDay>>({});
  const [recentWorkDays, setRecentWorkDays] = useState<WorkDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showModal, setShowModal] = useState(false);
  const [currentWorkDay, setCurrentWorkDay] = useState<WorkDay>({
    date: '',
    locationId: '',
    transportToWorkId: '',
    transportToHomeId: '',
    notes: ''
  });
  const [alertMessage, setAlertMessage] = useState<{ type: string; message: string } | null>(null);

  const [transportMeans, setTransportMeans] = useState<TransportMeans[]>([]);
  // Statistics year selection
  const [statsYear, setStatsYear] = useState<number>(new Date().getFullYear());
  const [showYearModal, setShowYearModal] = useState(false);
  const [modalYear, setModalYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    if (!currentUser) return;

    // Fetch work locations
    const locationsRef = ref(database, `work/${currentUser.uid}/work_locations`);
    const unsubscribeLocations = onValue(locationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const locationsList = Object.entries(data).map(([id, location]: [string, any]) => ({
        id,
        ...location
      }));
      setWorkLocations(locationsList);
    });

    // Fetch transport means
    const transportRef = ref(database, `work/${currentUser.uid}/work_meansoftransport`);
    const unsubscribeTransport = onValue(transportRef, (snapshot) => {
      const data = snapshot.val() || {};
      const transportList = Object.entries(data).map(([id, transport]: [string, any]) => ({
        id,
        name: transport.name || '',
        vega: transport.vega || false
      }));
      setTransportMeans(transportList);
    });

    // Fetch work days
    const workDaysRef = ref(database, `work/${currentUser.uid}/work_calendar`);
    const unsubscribeWorkDays = onValue(workDaysRef, (snapshot) => {
      const data = snapshot.val() || {};
      setWorkDays(data);
      
      // Process recent work days for the dashboard
      const workDaysList = Object.entries(data)
        .map(([date, day]: [string, any]) => ({
          date,
          ...day
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5); // Get only the 5 most recent entries
      
      setRecentWorkDays(workDaysList);
    });

    return () => {
      unsubscribeLocations();
      unsubscribeWorkDays();
      unsubscribeTransport();
    };
  }, [currentUser]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get location name by ID
  const getLocationName = (locationId: string) => {
    const location = workLocations.find(loc => loc.id === locationId);
    return location ? location.name : 'Unbekannt';
  };
  
  // Get transport means name by ID
  const getTransportName = (transportId?: string) => {
    if (!transportId) return '';
    const transport = transportMeans.find(t => t.id === transportId);
    return transport ? transport.name : 'Unbekannt';
  };

  // Get location color by ID
  const getLocationColor = (locationId: string) => {
    const location = workLocations.find(loc => loc.id === locationId);
    return location ? location.color : '#cccccc';
  };

  // Helper: filter by selected statistics year (robust: parse year from YYYY-MM-DD string)
  const isInSelectedYear = (dateString: string) => {
    const year = parseInt(dateString.slice(0, 4), 10);
    return year === statsYear;
  };

  const handleDateClick = (date: Date) => {
    // Create a local date that preserves the day
    const localDate = new Date(date);
    
    // Format date as YYYY-MM-DD using local timezone
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    console.log('Date:', localDate);
    console.log('Selected date:', dateString);

    const existingWorkDay = workDays[dateString] || {
      date: dateString,
      locationId: '',
      transportToWorkId: '',
      transportToHomeId: '',
      notes: ''
    };
    
    setSelectedDate(date);
    setCurrentWorkDay(existingWorkDay);
    setShowModal(true);
  };

  // Increment VeGa counter
  const incrementCounter = async (date?: Date) => {
    if (!currentUser) return;
    
    try {
      // Use provided date or current date
      const dateToUse = date ? new Date(date) : new Date();
      // Set time-of-day to the current time (not 12:00)
      if (date) {
        const now = new Date();
        dateToUse.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      }
      const timestamp = Math.floor(dateToUse.getTime() / 1000);
      
      const countsRef = ref(database, `vega/${currentUser.uid}/counts`);
      await push(countsRef, timestamp);
    } catch (error) {
      console.error('Error updating VeGa counter:', error);
    }
  };

  const handleSaveWorkDay = async () => {
    if (!currentUser || !currentWorkDay.date) return;

    try {
      const dateString = currentWorkDay.date;
      const workDayRef = ref(database, `work/${currentUser.uid}/work_calendar/${dateString}`);
      
      // Save the work day entry
      await set(workDayRef, currentWorkDay);
      
      // Check if any of the selected transports has VeGa property set to true
      let vegaRidesCount = 0;
      
      // Check transport to work
      if (currentWorkDay.transportToWorkId) {
        const transportToWork = transportMeans.find(t => t.id === currentWorkDay.transportToWorkId);
        if (transportToWork && transportToWork.vega) {
          vegaRidesCount++;
        }
      }
      
      // Check transport to home
      if (currentWorkDay.transportToHomeId) {
        const transportToHome = transportMeans.find(t => t.id === currentWorkDay.transportToHomeId);
        if (transportToHome && transportToHome.vega) {
          vegaRidesCount++;
        }
      }
      
      // Add VeGa rides if any transport with VeGa property was selected
      if (vegaRidesCount > 0) {
        // Add VeGa rides for each transport with VeGa property
        // Use the selected date for the VeGa ride
        for (let i = 0; i < vegaRidesCount; i++) {
          await incrementCounter(new Date(dateString));
        }
        
        // Show success message with VeGa rides info
        setAlertMessage({
          type: 'success',
          message: `Arbeitsort gespeichert und ${vegaRidesCount} VeGa ${vegaRidesCount === 1 ? 'Fahrt' : 'Fahrten'} erfasst`
        });
      } else {
        // Show regular success message
        setAlertMessage({
          type: 'success',
          message: 'Arbeitsort erfolgreich gespeichert.'
        });
      }
      
      setShowModal(false);
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error saving work day:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Speichern des Arbeitsortes.'
      });
    }
  };

  const handleDeleteWorkDay = async () => {
    if (!currentUser || !currentWorkDay.date) return;

    try {
      const dateString = currentWorkDay.date;
      const workDayRef = ref(database, `work/${currentUser.uid}/work_calendar/${dateString}`);
      
      await remove(workDayRef);
      
      setShowModal(false);
      setAlertMessage({
        type: 'success',
        message: 'Arbeitsort erfolgreich gelöscht.'
      });
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting work day:', error);
      setAlertMessage({
        type: 'danger',
        message: 'Fehler beim Löschen des Arbeitsortes.'
      });
    }
  };

  const getTileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    // Format date as YYYY-MM-DD using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const workDay = workDays[dateString];
    
    if (!workDay || !workDay.locationId) return null;
    
    const location = workLocations.find(loc => loc.id === workDay.locationId);
    if (!location) return null;
    
    return (
      <div 
        style={{ 
          backgroundColor: location.color,
          width: '100%',
          height: '5px',
          position: 'absolute',
          bottom: 0,
          left: 0,
          borderRadius: '0 0 4px 4px'
        }} 
        title={location.name}
      />
    );
  };

  const getTileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return '';
    
    // Format date as YYYY-MM-DD using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    
    const workDay = workDays[dateString];
    
    if (!workDay || !workDay.locationId) return '';
    
    return 'has-work-location';
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Work Dashboard</h2>
        <Card>
          <Card.Body>
            <Card.Title style={{ color: '#1780a6' }}>Work Dashboard</Card.Title>
            <p>Bitte melden Sie sich an, um das Dashboard anzuzeigen.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="mb-4">Work Dashboard</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/work" 
            active={location.pathname === '/dashboard/work'}
          >
            Work Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/work-settings" 
            active={location.pathname === '/dashboard/work-settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      {alertMessage && (
        <Alert 
          variant={alertMessage.type} 
          onClose={() => setAlertMessage(null)} 
          dismissible 
          className="mb-4"
        >
          {alertMessage.message}
        </Alert>
      )}
      
      <Row>
        {/* Calendar */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Arbeitskalender</Card.Title>
              <p className="text-muted">Klicken Sie auf einen Tag, um den Arbeitsort einzutragen.</p>
              
              <div className="calendar-container">
                <Calendar
                  onChange={(value: any) => {
                    if (value instanceof Date) {
                      setSelectedDate(value);
                    } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof Date) {
                      setSelectedDate(value[0]);
                    }
                  }}
                  value={selectedDate}
                  onClickDay={handleDateClick}
                  tileContent={getTileContent}
                  tileClassName={getTileClassName}
                  locale="de-DE"
                  className="react-calendar custom-calendar"
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Recent Work Locations - moved up */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Letzte Arbeitsorte</Card.Title>
              {recentWorkDays.length > 0 ? (
                <div className="mt-3">
                  {recentWorkDays.map((day) => (
                    <div 
                      key={day.date} 
                      className="d-flex justify-content-between align-items-center mb-2 p-2"
                      style={{ 
                        borderLeft: `4px solid ${getLocationColor(day.locationId)}`,
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px'
                      }}
                    >
                      <div>
                        <div className="fw-bold">{formatDate(day.date)}</div>
                        {day.notes && <small className="text-muted">{day.notes}</small>}
                      </div>
                      <Badge 
                        bg="light" 
                        text="dark" 
                        style={{ 
                          backgroundColor: getLocationColor(day.locationId),
                          color: '#fff'
                        }}
                      >
                        {getLocationName(day.locationId)}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mt-3">Keine Einträge vorhanden</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Statistics Section */}
      <h3 className="mb-3 mt-4">Statistik</h3>
      <Row>
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <Card.Title style={{ color: '#1780a6' }}>Statistik Arbeitsorte</Card.Title>
                <Badge bg="primary" pill style={{ cursor: 'pointer' }} onClick={() => { setModalYear(statsYear); setShowYearModal(true); }}>
                  {statsYear}
                </Badge>
              </div>
              <div className="mt-3">
                {workLocations.length > 0 ? (
                  workLocations.map(location => {
                    // Count days with this location
                    const daysCount = Object.entries(workDays)
                      .filter(([date, day]) => isInSelectedYear(date) && (day as WorkDay).locationId === location.id)
                      .length;
                    
                    return (
                      <div key={location.id} className="mb-3">
                        <div className="d-flex justify-content-between">
                          <span>{location.name}</span>
                          <span>{daysCount} Tage</span>
                        </div>
                        <div 
                          style={{
                            height: '8px',
                            width: `${Math.min(100, daysCount * 2)}%`,
                            backgroundColor: location.color,
                            borderRadius: '4px',
                            marginTop: '5px'
                          }}
                        />
                      </div>
                    );
                  })
                ) : (
                  <p className="text-muted text-center">Keine Daten verfügbar</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <Card.Title style={{ color: '#1780a6' }}>Statistik Arbeitswege</Card.Title>
                <Badge bg="primary" pill style={{ cursor: 'pointer' }} onClick={() => { setModalYear(statsYear); setShowYearModal(true); }}>
                  {statsYear}
                </Badge>
              </div>
              <div className="mt-3">
                {transportMeans.length > 0 ? (
                  <>
                    {transportMeans.map(transport => {
                      // Count days with this transport (either to work or to home)
                      const filteredEntries = Object.entries(workDays).filter(([date]) => isInSelectedYear(date));
                      const toWorkCount = filteredEntries.filter(([, day]) => (day as WorkDay).transportToWorkId === transport.id).length;
                      const toHomeCount = filteredEntries.filter(([, day]) => (day as WorkDay).transportToHomeId === transport.id).length;
                      const totalCount = toWorkCount + toHomeCount;
                      const totalTripsInYear = filteredEntries.length * 2; // each day has two trips
                      const percentage = totalTripsInYear > 0 ? (totalCount / totalTripsInYear) * 100 : 0;
                      
                      return (
                        <div key={transport.id} className="mb-3">
                          <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                              <span>{transport.name}</span>
                              {transport.vega && (
                                <Badge bg="success" className="ms-2" pill>VeGa</Badge>
                              )}
                            </div>
                            <span>{totalCount} Fahrten ({Math.round(percentage)}%)</span>
                          </div>
                          <div className="d-flex align-items-center mt-1">
                            <div 
                              style={{
                                height: '8px',
                                width: `${Math.min(100, percentage)}%`,
                                backgroundColor: transport.vega ? '#28a745' : '#6c757d',
                                borderRadius: '4px',
                                marginRight: '8px'
                              }}
                            />
                          </div>
                          <div className="d-flex justify-content-between small text-muted mt-1">
                            <span>Zur Arbeit: {toWorkCount}</span>
                            <span>Nach Hause: {toHomeCount}</span>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <p className="text-muted text-center">Keine Daten verfügbar</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Legend Section */}
      <h3 className="mb-3 mt-4">Legende</h3>
      <Row>
        {/* Work Locations Legend */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Arbeitsorte</Card.Title>
              {workLocations.length > 0 ? (
                <div className="mt-3">
                  {workLocations.map(location => (
                    <div key={location.id} className="d-flex align-items-center mb-2">
                      <div 
                        style={{
                          backgroundColor: location.color,
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          marginRight: '10px'
                        }}
                      />
                      <span>{location.name}</span>
                    </div>
                  ))}
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => window.location.href = "/dashboard/work-settings"}
                    >
                      Arbeitsorte verwalten
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted">Keine Arbeitsorte konfiguriert</p>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => window.location.href = "/dashboard/work-settings"}
                  >
                    Arbeitsorte konfigurieren
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Transport Means Legend */}
        <Col md={6} className="mb-4">
          <Card>
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Arbeitswege</Card.Title>
              {transportMeans.length > 0 ? (
                <div className="mt-3">
                  <div className="row">
                    {transportMeans.map(transport => (
                      <div key={transport.id} className="col-md-6 mb-3">
                        <div className="d-flex align-items-center">
                          <div 
                            style={{
                              backgroundColor: transport.vega ? '#28a745' : '#6c757d',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              marginRight: '8px'
                            }}
                          />
                          <span>{transport.name}</span>
                          {transport.vega && (
                            <Badge bg="success" className="ms-2" pill>VeGa</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => window.location.href = "/dashboard/work-settings"}
                    >
                      Transportmittel verwalten
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted">Keine Transportmittel konfiguriert</p>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => window.location.href = "/dashboard/work-settings"}
                  >
                    Transportmittel konfigurieren
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Statistikjahr wählen */}
      <Modal show={showYearModal} onHide={() => setShowYearModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Statistikjahr auswählen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Jahr</Form.Label>
              {/* Mobile: list of years */}
              <div className="d-md-none" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {Array.from(new Set(
                  Object.keys(workDays)
                    .map(d => parseInt(d.slice(0, 4), 10))
                    .concat([new Date().getFullYear()])
                ))
                  .sort((a, b) => b - a)
                  .map((y) => (
                    <Button
                      key={y}
                      variant={modalYear === y ? 'primary' : 'outline-secondary'}
                      className="w-100 mb-2"
                      onClick={() => setModalYear(y)}
                    >
                      {y}
                    </Button>
                  ))}
              </div>
              {/* Desktop: dropdown */}
              <div className="d-none d-md-block">
                <Form.Select
                  value={modalYear}
                  onChange={(e) => setModalYear(parseInt(e.target.value, 10))}
                >
                  {Array.from(new Set(
                    Object.keys(workDays)
                      .map(d => parseInt(d.slice(0, 4), 10))
                      .concat([new Date().getFullYear()])
                  ))
                    .sort((a, b) => b - a)
                    .map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                </Form.Select>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowYearModal(false)}>Abbrechen</Button>
          <Button variant="primary" onClick={() => { setStatsYear(modalYear); setShowYearModal(false); }}>Übernehmen</Button>
        </Modal.Footer>
      </Modal>

      {/* Work Day Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Arbeitsort für {selectedDate.toLocaleDateString('de-DE')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeWorkLocations.length > 0 ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Arbeitsort</Form.Label>
                <Form.Select
                  value={currentWorkDay.locationId}
                  onChange={(e) => {
                    const locationId = e.target.value;
                    const selectedLocation = activeWorkLocations.find(loc => loc.id === locationId);
                    
                    // Check if the selected location is Home Office
                    if (selectedLocation && selectedLocation.name === "Home Office") {
                      // Find or create an N.A. transport option
                      const naTransport = transportMeans.find(t => t.name === "N.A.");
                      const naTransportId = naTransport ? naTransport.id : "";
                      
                      // Set both transport fields to N.A.
                      setCurrentWorkDay({ 
                        ...currentWorkDay, 
                        locationId, 
                        transportToWorkId: naTransportId,
                        transportToHomeId: naTransportId 
                      });
                    } else {
                      // Normal behavior for other locations
                      setCurrentWorkDay({ ...currentWorkDay, locationId });
                    }
                  }}
                >
                  <option value="">Bitte wählen...</option>
                  {activeWorkLocations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Transportmittel zur Arbeit</Form.Label>
                {transportMeans.length > 0 ? (
                  <Form.Select
                    value={currentWorkDay.transportToWorkId || ''}
                    onChange={(e) => setCurrentWorkDay({ ...currentWorkDay, transportToWorkId: e.target.value })}
                    disabled={workLocations.find(loc => loc.id === currentWorkDay.locationId)?.name === "Home Office"}
                  >
                    <option value="">Bitte wählen...</option>
                    {transportMeans.map(transport => (
                      <option key={transport.id} value={transport.id}>
                        {transport.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className="text-muted small mb-2">
                    Keine Transportmittel konfiguriert. 
                    <Link to="/dashboard/work-settings" className="ms-1">Transportmittel hinzufügen</Link>
                  </div>
                )}
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Transportmittel nach Hause</Form.Label>
                {transportMeans.length > 0 ? (
                  <Form.Select
                    value={currentWorkDay.transportToHomeId || ''}
                    onChange={(e) => setCurrentWorkDay({ ...currentWorkDay, transportToHomeId: e.target.value })}
                    disabled={workLocations.find(loc => loc.id === currentWorkDay.locationId)?.name === "Home Office"}
                  >
                    <option value="">Bitte wählen...</option>
                    {transportMeans.map(transport => (
                      <option key={transport.id} value={transport.id}>
                        {transport.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className="text-muted small mb-2">
                    Keine Transportmittel konfiguriert. 
                    <Link to="/dashboard/work-settings" className="ms-1">Transportmittel hinzufügen</Link>
                  </div>
                )}
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Notizen</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={currentWorkDay.notes || ''}
                  onChange={(e) => setCurrentWorkDay({ ...currentWorkDay, notes: e.target.value })}
                  placeholder="Optionale Notizen zum Arbeitstag"
                />
              </Form.Group>
            </Form>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">Keine Arbeitsorte konfiguriert</p>
              <Button 
                variant="primary" 
                onClick={() => {
                  setShowModal(false);
                  window.location.href = "/dashboard/work-settings";
                }}
              >
                Arbeitsorte konfigurieren
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            {currentWorkDay.date && Object.keys(workDays).includes(currentWorkDay.date) && (
              <Button 
                variant="danger" 
                onClick={handleDeleteWorkDay}
              >
                Löschen
              </Button>
            )}
            <div>
              <Button variant="secondary" onClick={() => setShowModal(false)} className="me-2">
                Abbrechen
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSaveWorkDay}
                disabled={!currentWorkDay.locationId || workLocations.length === 0}
              >
                Speichern
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default WorkDashboard;
